import { useCallback, useEffect, useRef, useState } from 'react'
import Modal from 'react-modal'
import { TextField } from '@mui/material'
import adminRequest from '../../utils/adminRequest'
import formatMoney, { toFiniteAmount } from '../../utils/formatMoney'
import LoadingIndicator from '../LoadingIndicator'

const OPTIONS_TIMEOUT_MS = 8000

export const CONSENT_NOTE_MAX_LENGTH = 500

const ETA_LABELS = {
  up_to_15_business_days: 'Puede demorar hasta 15 días hábiles',
  usually_instant: 'Suele ser al instante, pero puede demorar entre 2 y 7 días según el banco',
  instant: 'Al instante',
  unknown: 'El plazo depende del medio de pago'
}

const WALLET_NOT_WITHDRAWABLE_NOTICE =
  'El saldo Viatik no se puede retirar ni transferir: confirmalo con el pasajero antes de elegirlo.'

const FORCED_WALLET_TEXT = 'Se devuelve a su saldo (pagó con saldo).'

const ALREADY_REFUNDED = 'already_refunded'

const ALREADY_REFUNDED_TEXT = 'Ya tiene un reembolso hecho o en curso: no se le devuelve nada más.'

const OPTIONS_FAILED_TEXT = 'No pudimos cargar las opciones de reembolso. Reintentá antes de confirmar.'

const WALLET_BLOCK_REASONS = {
  seats_added: 'es un pago de asientos agregados',
  double_charge: 'tiene un cobro duplicado',
  amount_mismatch: 'los montos del pago no coinciden',
  disabled: 'los reembolsos de pagos con saldo están pausados',
  not_approved: 'el pago no está aprobado',
  already_refunded: 'ya tiene un reembolso hecho o en curso',
  invalid_amount: 'el monto a devolver no es válido',
  paid_amount_unavailable: 'falta el monto cobrado',
  not_found: 'no encontramos la transacción',
  read_failed: 'no pudimos leer la transacción',
  invalid_currency: 'la moneda del pago no admite saldo',
  alternative_payment: 'se pagó por depósito o transferencia',
  not_owner: 'el pago es de otro usuario',
  provider_state: 'no pudimos confirmar el estado del pago'
}

const walletBlockedText = reason => {
  const label = typeof reason === 'string' && reason ? WALLET_BLOCK_REASONS[reason] || reason : null
  const detail = label ? ` (${label})` : ''
  return `Este pago se hizo con saldo y no se puede devolver al saldo automáticamente${detail}. Resolvelo a mano.`
}

const walletShareText = amountText => `${amountText} vuelven a su saldo (la parte que pagó con saldo).`

const ACTION_COPY = {
  delete: {
    title: name => `Eliminar a ${name} del viaje`,
    description: 'Sale del viaje y se le devuelve el total que pagó.',
    freeDescription: 'Sale del viaje. Viajaba gratis, así que no hay nada para devolver.',
    refundedDescription: 'Sale del viaje.',
    confirm: 'Eliminar y reembolsar',
    confirmFree: 'Eliminar del viaje',
    confirmBlocked: 'Eliminar',
    done: 'Acompañante eliminado'
  },
  partial: {
    title: name => `Reembolso 50% a ${name}`,
    description:
      'Se le devuelve el 50% de lo pagado, sale del viaje y se ajusta el pago del conductor.',
    confirm: 'Reembolsar 50%',
    confirmBlocked: 'Confirmar',
    done: 'Reembolso parcial enviado'
  }
}

const WALLET_UNAVAILABLE_MESSAGES = {
  provider_state:
    'No se puede devolver a saldo: no pudimos confirmar con Mercado Pago que el pago siga sin devoluciones ni reclamos. Va al medio de pago original: confirmá de nuevo.',
  default: 'No se puede devolver a saldo para este pago. Va al medio de pago original: confirmá de nuevo.'
}

const ERROR_MESSAGES = {
  refund_in_progress: 'Esta transacción ya tiene un reembolso en curso. No se generó otro.',
  invalid_refund_method: 'La opción de reembolso no es válida. Cerrá y volvé a abrir el panel.',
  invalid_consent_note: `Completá el pedido del pasajero (hasta ${CONSENT_NOTE_MAX_LENGTH} caracteres).`,
  consent_note_required: `Completá el pedido del pasajero (hasta ${CONSENT_NOTE_MAX_LENGTH} caracteres).`,
  free_transaction: 'Este acompañante viajaba gratis: no hay nada para reembolsar.',
  refund_failed: 'No se pudo hacer el reembolso. No se cambió nada en el viaje.',
  refunds_paused:
    'Los reembolsos de pagos con saldo están pausados. No se cambió nada en el viaje: reintentá más tarde.'
}

const LEGACY_ERROR_MESSAGES = {
  'Transaction already refunded': 'Esta transacción ya fue reembolsada.',
  'Transaction is not approved': 'La transacción no está aprobada: no se puede reembolsar el 50%.',
  'Companion not found in lobby': 'El acompañante ya no está en este viaje. Actualizá el panel.'
}

const UNCERTAIN_RESULT =
  'No pudimos confirmar el resultado. Revisá las transacciones del viaje antes de reintentar.'

const UNCONFIRMED_REFUND_RESULT =
  'No pudimos confirmar el resultado. Revisá Mercado Pago antes de reintentar.'

const UNCONFIRMED_REFUND_ERRORS = ['refund_unconfirmed', 'claim_unconfirmed']

export const FAILED_REFUND_TEXT =
  'No pudimos hacer el reembolso automático. Abrí las transacciones del viaje: si figura «Reembolso en proceso» o «Reembolsado», no hagas nada; si sigue «Aprobado» sin reembolso, devolvelo desde Mercado Pago (no por transferencia).'

export const FAILED_FORCED_REFUND_TEXT =
  'No pudimos devolverle a su saldo lo que pagó. Abrí las transacciones del viaje: si figura «Reembolso en proceso» o «Reembolsado», no hagas nada; si sigue «Aprobado» sin reembolso, avisá a pagos. No lo devuelvas por Mercado Pago ni por transferencia: pagó sólo con saldo.'

const failedMixedRefundText = split =>
  `No pudimos hacer el reembolso automático. Abrí las transacciones del viaje: si figura «Reembolso en proceso» o «Reembolsado», no hagas nada; si sigue «Aprobado» sin reembolso, devolvé ${split.provider} desde Mercado Pago (no por transferencia) y avisá a pagos por los ${split.wallet} que pagó con saldo.`

const ALREADY_REFUNDING_TEXT =
  'Ya había otro reembolso en curso para esta transacción, así que no se generó uno nuevo. No lo devuelvas a mano: seguilo en las transacciones del viaje.'

const REVIEW_STATUSES = ['failed', 'processing', 'already_refunding']

export const needsTransactionReview = outcome =>
  Boolean(outcome?.result && !outcome.result.free && REVIEW_STATUSES.includes(outcome.result.status))

const modalStyles = {
  content: {
    borderRadius: '1.5rem',
    height: 'fit-content',
    maxHeight: '90vh',
    maxWidth: 560,
    margin: 'auto',
    top: 16,
    bottom: 16,
    left: 16,
    right: 16,
    padding: '1.75rem',
    overflow: 'auto'
  },
  overlay: {
    zIndex: 400,
    backgroundColor: 'rgba(0, 0, 0, 0.5)'
  }
}

const RESULT_TONES = {
  emerald: 'bg-emerald/10 text-emerald_deep ring-emerald/30 dark:bg-emerald-500/10 dark:text-emerald-400',
  amber: 'bg-amber/[0.18] text-amber-700 ring-amber/40 dark:bg-amber-500/10 dark:text-amber-400',
  alert: 'bg-coral/[0.12] text-coral_deep ring-coral/30 dark:bg-red-500/10 dark:text-red-400',
  neutral: 'bg-ink/[0.05] text-espresso ring-ink/10 dark:bg-dk-raised dark:text-gray-200 dark:ring-dk-border'
}

export const isFreeCompanion = companion =>
  !companion?.transaction_id || companion.transaction_id === 'free'

const companionTotal = companion =>
  (Number(companion?.price) || 0) + (Number(companion?.fee) || 0) - (Number(companion?.discount) || 0)

const companionName = companion =>
  [companion?.first_name, companion?.last_name].filter(Boolean).join(' ') || 'este acompañante'

export const parseRefundOptions = data => {
  const source = data && typeof data === 'object' && 'refund_options' in data ? data.refund_options : data
  if (!source || typeof source !== 'object' || !Array.isArray(source.methods)) return null
  return source
}

const findMethod = (options, method) =>
  options?.methods?.find(item => item && item.method === method) || null

const originalLabelSuffix = label =>
  typeof label === 'string' && label && !/^tu /i.test(label) ? ` (${label})` : ''

const roundMoney = value => Math.round(value * 100) / 100

const positiveAmount = value => {
  const amount = toFiniteAmount(value)
  return amount !== null && amount > 0 ? amount : 0
}

export const refundWalletShare = ({ options, amount, partial }) => {
  const total = positiveAmount(amount)
  if (total <= 0) return 0
  if (options?.forced === true) return total
  const wallet = positiveAmount(options?.wallet_amount)
  if (wallet <= 0) return 0
  if (!partial) return Math.min(wallet, total)
  const provider = positiveAmount(options?.provider_amount)
  const paid = positiveAmount(options?.paid_amount) || roundMoney(wallet + provider)
  if (provider <= 0) return total
  return roundMoney(total - Math.min(provider, Math.round((total * provider) / paid)))
}

const splitOf = result => {
  const total = positiveAmount(result.amount)
  const wallet = Math.min(positiveAmount(result.walletAmount), total)
  if (wallet <= 0) return null
  const provider = toFiniteAmount(result.providerAmount) ?? roundMoney(total - wallet)
  return {
    wallet: formatMoney(wallet, result.currency),
    provider: formatMoney(provider, result.currency)
  }
}

export const describeRefundResult = ({ result, requestedMethod, forced = false }) => {
  if (!result || result.free) {
    return { tone: 'neutral', text: 'Viajaba gratis: no había nada para reembolsar.' }
  }
  const amount = formatMoney(result.amount, result.currency) || 'el reembolso'
  if (result.status === 'already_refunding') {
    return { tone: 'amber', text: ALREADY_REFUNDING_TEXT }
  }
  if (result.status === 'failed') {
    if (forced) return { tone: 'alert', text: FAILED_FORCED_REFUND_TEXT }
    const failedSplit = splitOf(result)
    return { tone: 'alert', text: failedSplit ? failedMixedRefundText(failedSplit) : FAILED_REFUND_TEXT }
  }
  if (forced || result.method === 'wallet') {
    if (result.status === 'processing') {
      return {
        tone: 'amber',
        text: `El crédito de ${amount} a su saldo Viatik está en proceso. Se completa solo en unos minutos.`
      }
    }
    return { tone: 'emerald', text: `Le devolvimos ${amount} a su saldo Viatik.` }
  }
  const fellBack = requestedMethod === 'wallet' ? 'No se pudo usar el saldo. ' : ''
  const split = splitOf(result)
  if (result.status === 'processing') {
    return {
      tone: 'amber',
      text: split
        ? `${fellBack}El reembolso de ${split.provider} al medio de pago original quedó en proceso y se confirma solo, junto con los ${split.wallet} que vuelven a su saldo. No lo devuelvas a mano.`
        : `${fellBack}El reembolso de ${amount} al medio de pago original quedó en proceso y se confirma solo. No lo devuelvas a mano.`
    }
  }
  if (result.legacy) {
    return { tone: 'neutral', text: 'El reembolso sale al medio de pago original.' }
  }
  const suffix = originalLabelSuffix(result.label)
  return {
    tone: fellBack ? 'amber' : 'emerald',
    text: split
      ? `${fellBack}Solicitamos el reembolso de ${split.provider} al medio de pago original${suffix} y le devolvimos ${split.wallet} a su saldo Viatik (la parte que pagó con saldo).`
      : `${fellBack}Solicitamos el reembolso de ${amount} al medio de pago original${suffix}.`
  }
}

const walletPartsOf = (source, fallback) => ({
  walletAmount: toFiniteAmount(source?.wallet_amount) ?? fallback.walletAmount ?? 0,
  providerAmount: toFiniteAmount(source?.provider_amount)
})

const parseResult = ({ action, data, fallback }) => {
  const body = data && typeof data === 'object' ? data : null
  if (action === 'partial') {
    if (!body) return { ...fallback, status: 'completed', legacy: true }
    return {
      method: body.refund_method || fallback.method,
      status: body.refund_status || 'completed',
      amount: toFiniteAmount(body.refundAmount) ?? fallback.amount,
      currency: fallback.currency,
      label: fallback.label,
      ...walletPartsOf(body, fallback),
      legacy: !body.refund_status
    }
  }
  if (!body || !('refund' in body)) return { ...fallback, legacy: true }
  if (body.refund === null) return { free: true }
  const refund = body.refund && typeof body.refund === 'object' ? body.refund : {}
  return {
    method: refund.method || fallback.method,
    status: refund.status || 'completed',
    amount: toFiniteAmount(refund.amount) ?? fallback.amount,
    currency: refund.currency || fallback.currency,
    label: refund.label || fallback.label,
    ...walletPartsOf(refund, fallback)
  }
}

const errorCodeOf = response => (typeof response.data?.error === 'string' ? response.data.error : null)

const isUnconfirmedRefund = response => {
  const code = errorCodeOf(response)
  if (UNCONFIRMED_REFUND_ERRORS.includes(code)) return true
  return code === 'refund_failed' && response.data.reason === 'refund_unconfirmed'
}

const uncertainError = text => ({ text, uncertain: true })

const definitiveError = text => ({ text, uncertain: false })

const describeError = response => {
  if (response.networkError) return uncertainError(UNCERTAIN_RESULT)
  if (isUnconfirmedRefund(response)) return uncertainError(UNCONFIRMED_REFUND_RESULT)
  const code = errorCodeOf(response)
  if (code === 'wallet_unavailable') {
    return definitiveError(
      response.data.reason === 'provider_state'
        ? WALLET_UNAVAILABLE_MESSAGES.provider_state
        : WALLET_UNAVAILABLE_MESSAGES.default
    )
  }
  if (code && ERROR_MESSAGES[code]) return definitiveError(ERROR_MESSAGES[code])
  if (typeof response.data === 'string' && LEGACY_ERROR_MESSAGES[response.data.trim()]) {
    return definitiveError(LEGACY_ERROR_MESSAGES[response.data.trim()])
  }
  if (response.status === 401 || response.status === 403) {
    return definitiveError('No tenés permiso para esta acción o tu sesión venció. Recargá la página.')
  }
  if (response.status === 404) {
    return definitiveError('El acompañante ya no está en este viaje. Actualizá el panel.')
  }
  if (response.status >= 500) return uncertainError(UNCERTAIN_RESULT)
  return definitiveError('No se pudo completar la acción. Reintentá.')
}

export const buildRefundRequest = ({ action, lobby, companion, method, note, forced = false }) => {
  const walletFields = method === 'wallet' && !forced ? { consent_note: note.trim() } : {}
  if (action === 'partial') {
    return {
      path: '/admin/lobbies/partial-refund',
      method: 'POST',
      body: {
        lobby_id: lobby._id,
        transaction_id: companion.transaction_id,
        refund_method: method,
        ...walletFields
      }
    }
  }
  return {
    path: `/admin/lobbies/companions/${lobby._id}`,
    method: 'DELETE',
    body: { user_id: companion.user_id, refund_method: method, ...walletFields }
  }
}

const useRefundOptions = ({ lobbyId, userId, skip }) => {
  const [attempt, setAttempt] = useState(0)
  const [state, setState] = useState({ status: skip ? 'unavailable' : 'loading', options: null })

  useEffect(() => {
    if (skip) return undefined
    let active = true
    const controller = typeof AbortController === 'function' ? new AbortController() : null
    const timeout = setTimeout(() => controller?.abort(), OPTIONS_TIMEOUT_MS)
    adminRequest(
      `/admin/lobbies/refund-options/${lobbyId}?user_id=${encodeURIComponent(userId)}`,
      { signal: controller?.signal }
    ).then(response => {
      if (!active) return
      if (response.networkError || response.status >= 500) {
        setState({ status: 'failed', options: null })
        return
      }
      const options = response.ok ? parseRefundOptions(response.data) : null
      setState({ status: options ? 'ready' : 'unavailable', options })
    })
    return () => {
      active = false
      clearTimeout(timeout)
      controller?.abort()
    }
  }, [lobbyId, userId, skip, attempt])

  const retry = useCallback(() => {
    setState({ status: 'loading', options: null })
    setAttempt(value => value + 1)
  }, [])

  return { ...state, retry }
}

const destinationOf = ({ free, optionsStatus, options, forcedRejection }) => {
  if (free) return 'free'
  if (forcedRejection) return 'wallet_blocked'
  if (optionsStatus === 'loading' || optionsStatus === 'failed') return optionsStatus
  if (optionsStatus !== 'ready') return 'original'
  if (options.reason === ALREADY_REFUNDED) return 'already_refunded'
  const walletAvailable = Boolean(findMethod(options, 'wallet'))
  if (options.forced === true) return walletAvailable ? 'forced' : 'wallet_blocked'
  return walletAvailable ? 'selector' : 'original'
}

const BLOCKED_DESTINATIONS = ['loading', 'failed', 'wallet_blocked']

function DestinationOption({ value, checked, disabled, onChange, title, hint, notice, describedBy }) {
  const noticeId = notice ? `${describedBy}-notice` : null
  return (
    <label
      className={`flex items-start gap-3 rounded-xl px-3.5 py-3 ring-1 transition-colors ${
        disabled
          ? 'cursor-not-allowed opacity-50 ring-warm_border dark:ring-dk-border'
          : checked
          ? 'cursor-pointer ring-coral/60 bg-coral/[0.06] dark:bg-red-500/10 dark:ring-coral/50'
          : 'cursor-pointer ring-warm_border dark:ring-dk-border hover:bg-ink/[0.03] dark:hover:bg-dk-hover'
      }`}
    >
      <input
        type='radio'
        name='refund-destination'
        value={value}
        checked={checked}
        disabled={disabled}
        onChange={() => onChange(value)}
        aria-describedby={[describedBy, noticeId].filter(Boolean).join(' ')}
        className='mt-1 accent-coral'
      />
      <span className='flex flex-col gap-0.5'>
        <span className='text-sm font-semibold text-espresso dark:text-gray-100'>{title}</span>
        <span id={describedBy} className='text-xs text-warm_gray dark:text-gray-400'>
          {hint}
        </span>
        {notice && (
          <span id={noticeId} className='mt-1 text-xs font-semibold text-espresso dark:text-gray-200'>
            {notice}
          </span>
        )}
      </span>
    </label>
  )
}

function ActionButton({ variant = 'primary', ...props }) {
  const styles =
    variant === 'primary'
      ? 'bg-coral text-white hover:bg-coral_deep disabled:bg-gray-300 disabled:text-white dark:disabled:bg-dk-hover dark:disabled:text-gray-500'
      : 'text-espresso dark:text-gray-200 hover:bg-ink/[0.05] dark:hover:bg-dk-hover disabled:opacity-50'
  return (
    <button
      type='button'
      {...props}
      className={`rounded-full px-5 py-2.5 text-sm font-bold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-coral/40 disabled:cursor-not-allowed ${styles}`}
    />
  )
}

export default function RefundActionDialog({
  action,
  lobby,
  companion,
  onClose,
  onDone,
  onOpenTransactions
}) {
  const copy = ACTION_COPY[action] || ACTION_COPY.delete
  const free = isFreeCompanion(companion)
  const {
    status: optionsStatus,
    options,
    retry: retryOptions
  } = useRefundOptions({
    lobbyId: lobby._id,
    userId: companion.user_id,
    skip: free
  })
  const [method, setMethod] = useState('original')
  const [note, setNote] = useState('')
  const [walletBlocked, setWalletBlocked] = useState(false)
  const [forcedRejection, setForcedRejection] = useState(null)
  const [retryLocked, setRetryLocked] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)
  const [outcome, setOutcome] = useState(null)
  const submittingRef = useRef(false)

  const originalOption = findMethod(options, 'original')
  const optionsReady = optionsStatus === 'ready'
  const walletOnly = optionsReady && options?.forced === true
  const destination = destinationOf({ free, optionsStatus, options, forcedRejection })
  const forced = destination === 'forced'
  const showSelector = destination === 'selector'
  const alreadyRefunded = optionsReady && options?.reason === ALREADY_REFUNDED
  const refundsNothing = destination === 'already_refunded' || destination === 'wallet_blocked'
  const blockedReason = forcedRejection ? forcedRejection.reason : options?.reason
  const selectedMethod =
    forced || (showSelector && !walletBlocked && method === 'wallet') ? 'wallet' : 'original'
  const currency = options?.currency || lobby.currency
  const amount =
    action === 'partial'
      ? Math.round(companionTotal(companion) / 2)
      : toFiniteAmount(options?.amount) ?? companionTotal(companion)
  const amountText = formatMoney(amount, currency)
  const walletShare =
    optionsReady && !refundsNothing
      ? refundWalletShare({ options, amount, partial: action === 'partial' })
      : 0
  const mixed = !forced && walletShare > 0
  const providerShareText = formatMoney(roundMoney(amount - walletShare), currency)
  const trimmedNote = note.trim()
  const needsNote = selectedMethod === 'wallet' && !forced
  const removesOnly = action === 'delete' && (free || alreadyRefunded)
  const confirmBlocked =
    BLOCKED_DESTINATIONS.includes(destination) || (alreadyRefunded && action === 'partial')
  const canSubmit =
    !submitting &&
    !outcome &&
    !retryLocked &&
    !confirmBlocked &&
    (!needsNote || trimmedNote.length > 0)

  const handleRequestClose = () => {
    if (!submitting) onClose()
  }

  const submit = async () => {
    if (submittingRef.current || !canSubmit) return
    submittingRef.current = true
    setSubmitting(true)
    setError(null)
    const request = buildRefundRequest({
      action,
      lobby,
      companion,
      method: selectedMethod,
      note,
      forced: walletOnly
    })
    const response = await adminRequest(request.path, { method: request.method, body: request.body })
    setSubmitting(false)
    if (!response.ok) {
      submittingRef.current = false
      const walletUnavailable = response.data?.error === 'wallet_unavailable'
      if (walletUnavailable && (walletOnly || selectedMethod !== 'wallet')) {
        setForcedRejection({ reason: response.data.reason })
        return
      }
      if (walletUnavailable) {
        setWalletBlocked(true)
        setMethod('original')
      }
      const failure = describeError(response)
      if (failure.uncertain) setRetryLocked(true)
      setError(failure.text)
      return
    }
    const result = parseResult({
      action,
      data: response.data,
      fallback: {
        method: selectedMethod,
        amount,
        currency,
        label: originalOption?.label,
        walletAmount: walletShare
      }
    })
    const done = { action, result, requestedMethod: selectedMethod, forced: walletOnly }
    setOutcome(done)
    if (onDone) onDone(done)
  }

  const description = outcome ? describeRefundResult(outcome) : null
  const noRefund = alreadyRefunded || destination === 'wallet_blocked'
  const confirmLabel = submitting
    ? 'Procesando…'
    : removesOnly
    ? copy.confirmFree
    : noRefund
    ? copy.confirmBlocked
    : copy.confirm
  const actionDescription =
    action === 'delete' && free
      ? copy.freeDescription
      : action === 'delete' && noRefund
      ? copy.refundedDescription
      : noRefund
      ? null
      : copy.description

  return (
    <Modal
      isOpen
      onRequestClose={handleRequestClose}
      style={modalStyles}
      contentLabel={copy.title(companionName(companion))}
    >
      <div className='flex flex-col gap-5 text-left text-espresso dark:text-gray-100'>
        <div>
          <h2 className='text-lg font-bold tracking-tight'>
            {outcome ? copy.done : copy.title(companionName(companion))}
          </h2>
          {!outcome && actionDescription && (
            <p className='mt-1 text-sm text-warm_gray dark:text-gray-400'>{actionDescription}</p>
          )}
        </div>

        {outcome ? (
          <p
            role='status'
            className={`rounded-2xl px-4 py-3 text-sm font-medium ring-1 ${RESULT_TONES[description.tone]}`}
          >
            {description.text}
          </p>
        ) : (
          <>
            {!free && !refundsNothing && (
              <div className='flex items-baseline justify-between gap-4 border-y border-warm_border dark:border-dk-border py-3'>
                <span className='text-[11px] uppercase tracking-wider font-medium text-warm_gray dark:text-gray-400'>
                  Monto a devolver
                </span>
                <span className='text-xl font-bold tabular-nums' data-testid='refund-amount'>
                  {amountText}
                </span>
              </div>
            )}

            {destination === 'loading' && (
              <div className='flex items-center gap-2 text-sm text-warm_gray dark:text-gray-400'>
                <LoadingIndicator size={12} />
                Buscando opciones de reembolso…
              </div>
            )}

            {destination === 'failed' && (
              <div className='flex flex-wrap items-center justify-between gap-3'>
                <p role='alert' className='text-sm text-espresso dark:text-gray-200'>
                  {OPTIONS_FAILED_TEXT}
                </p>
                <ActionButton variant='ghost' onClick={retryOptions}>
                  Reintentar
                </ActionButton>
              </div>
            )}

            {forced && (
              <p className='text-sm text-espresso dark:text-gray-200' data-testid='refund-wallet-share'>
                {FORCED_WALLET_TEXT}
              </p>
            )}

            {destination === 'wallet_blocked' && (
              <p
                role='alert'
                className='rounded-2xl bg-coral/10 ring-1 ring-coral/30 px-4 py-2.5 text-sm font-medium text-coral_deep dark:text-red-400'
              >
                {walletBlockedText(blockedReason)}
              </p>
            )}

            {destination === 'already_refunded' && (
              <p className='text-sm text-espresso dark:text-gray-200'>{ALREADY_REFUNDED_TEXT}</p>
            )}

            {mixed && (
              <p className='text-sm text-espresso dark:text-gray-200' data-testid='refund-wallet-share'>
                {walletShareText(formatMoney(walletShare, currency))}
              </p>
            )}

            {destination === 'original' && (
              <p className='text-sm text-espresso dark:text-gray-200'>
                {mixed ? `Los ${providerShareText} restantes van` : 'Va'} al medio de pago original
                {originalLabelSuffix(originalOption?.label)}.
              </p>
            )}

            {showSelector && (
              <fieldset className='flex flex-col gap-2.5'>
                <legend className='mb-2.5 text-[11px] uppercase tracking-wider font-bold text-warm_gray dark:text-gray-400'>
                  {mixed ? `¿Adónde van los ${providerShareText} restantes?` : '¿Adónde va el reembolso?'}
                </legend>
                <DestinationOption
                  value='original'
                  checked={selectedMethod === 'original'}
                  onChange={setMethod}
                  title='Medio de pago original'
                  hint={[originalOption?.label, ETA_LABELS[originalOption?.eta]].filter(Boolean).join(' · ')}
                  describedBy='refund-destination-original-hint'
                />
                <DestinationOption
                  value='wallet'
                  checked={selectedMethod === 'wallet'}
                  disabled={walletBlocked}
                  onChange={setMethod}
                  title='Saldo Viatik'
                  hint={
                    walletBlocked
                      ? 'No disponible para este pago.'
                      : 'Queda en su cuenta Viatik al instante. Usalo sólo si el pasajero lo pidió.'
                  }
                  notice={selectedMethod === 'wallet' && !walletBlocked ? WALLET_NOT_WITHDRAWABLE_NOTICE : null}
                  describedBy='refund-destination-wallet-hint'
                />
                {selectedMethod === 'wallet' && (
                  <TextField
                    label='Pedido del pasajero (chat, ticket o detalle)'
                    value={note}
                    onChange={event => setNote(event.target.value)}
                    required
                    multiline
                    minRows={2}
                    size='small'
                    inputProps={{ maxLength: CONSENT_NOTE_MAX_LENGTH }}
                    helperText={`Obligatorio para devolver a saldo. Queda registrado. ${note.length}/${CONSENT_NOTE_MAX_LENGTH}`}
                    sx={{ mt: 1 }}
                  />
                )}
              </fieldset>
            )}

            {error && (
              <p
                role='alert'
                className='rounded-2xl bg-coral/10 ring-1 ring-coral/30 px-4 py-2.5 text-sm font-medium text-coral_deep dark:text-red-400'
              >
                {error}
              </p>
            )}
          </>
        )}

        <div className='flex flex-wrap items-center justify-end gap-2 pt-1'>
          {outcome ? (
            <>
              {onOpenTransactions && needsTransactionReview(outcome) && (
                <ActionButton
                  variant='ghost'
                  onClick={() => {
                    onClose()
                    onOpenTransactions()
                  }}
                >
                  Ver transacciones
                </ActionButton>
              )}
              <ActionButton onClick={onClose}>Listo</ActionButton>
            </>
          ) : (
            <>
              <ActionButton variant='ghost' onClick={handleRequestClose} disabled={submitting}>
                Cancelar
              </ActionButton>
              <ActionButton onClick={submit} disabled={!canSubmit} aria-busy={submitting}>
                {confirmLabel}
              </ActionButton>
            </>
          )}
        </div>
      </div>
    </Modal>
  )
}
